import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CalendarIcon, ClockIcon, MapPinIcon } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { Reveal } from '../components/ui/Reveal';
import { EmptyState } from '../components/ui/EmptyState';
import { CtaBand } from '../components/ui/CtaBand';
import { events, eventTypes } from '../data/events';
const stateOptions = ['All states', ...Array.from(new Set(events.map(event => event.state)))];
export function Events() {
  const [type, setType] = useState('All Events');
  const [state, setState] = useState('All states');
  const [showPast, setShowPast] = useState(false);
  const filtered = useMemo(() => events.filter(event => {
    const matchesType = type === 'All Events' || event.type === type;
    const matchesState = state === 'All states' || event.state === state;
    const matchesTime = showPast ? Boolean(event.past) : !event.past;
    return matchesType && matchesState && matchesTime;
  }), [type, state, showPast]);
  const reset = () => {
    setType('All Events');
    setState('All states');
    setShowPast(false);
  };
  return <>
      <PageHero eyebrow="Campaign events" title="Meet the campaign in person." intro="Rallies, town halls, roundtables and press events across all six zones. Most are open; some require registration." crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Events'
    }]} data-id="element-808" data-mp="1m3f5utewpbuh" />

      <section className="bg-sand-50 py-14 lg:py-20" aria-label="Event filters and listing" data-id="element-809" data-mp="ogg8egge3zvj">
        <div className="mx-auto max-w-content px-4 sm:px-6 lg:px-10" data-id="element-810" data-mp="134jctlesb3i8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between" data-id="element-811" data-mp="ydktse2evrba">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by event type" data-id="element-812" data-mp="ao0dk2xn785p">
              {eventTypes.map(item => {
              const active = type === item;
              return <button key={item} type="button" aria-pressed={active} onClick={() => setType(item)} className={`rounded-lg border px-3.5 py-2 text-[14px] font-medium transition-colors duration-150 ease-emphasis ${active ? 'border-green-600 bg-green-600 text-white' : 'border-sand-300 bg-white text-slate-600 hover:border-slate-500/50 hover:text-ink'}`} data-id="element-813" data-mp="vdb2hcbknvot">
                    {item}
                  </button>;
            })}
            </div>

            <div className="flex flex-wrap items-end gap-4" data-id="element-814" data-mp="lh7ye0x5tmz8">
              <div data-id="element-815" data-mp="qeraau6dxno0">
                <label htmlFor="event-state" className="mb-1.5 block text-[13px] font-medium text-ink" data-id="element-816" data-mp="qf9humyhzob8">
                  State
                </label>
                <select id="event-state" value={state} onChange={event => setState(event.target.value)} className="rounded-lg border border-sand-300 bg-white px-4 py-2.5 text-[14px] text-ink" data-id="element-817" data-mp="21slloxu9c6xe">
                  {stateOptions.map(option => <option key={option} value={option} data-id="element-818" data-mp="3cjrte0pacu90">
                      {option}
                    </option>)}
                </select>
              </div>
              <label className="flex items-center gap-2.5 pb-2.5 text-[14px] text-slate-600" data-id="element-819" data-mp="33lpo38yirffx">
                <input type="checkbox" checked={showPast} onChange={event => setShowPast(event.target.checked)} className="h-4 w-4 accent-[#056839]" data-id="element-820" data-mp="2ykjp4te9mvxy" />
                Show past events
              </label>
            </div>
          </div>

          <p className="mt-6 text-[14px] text-slate-500" role="status" data-id="element-821" data-mp="ruc6hgt6tpon">
            {filtered.length} {filtered.length === 1 ? 'event' : 'events'}
          </p>

          {filtered.length === 0 ? <div className="mt-6" data-id="element-822" data-mp="uxhxzlg2a292">
              <EmptyState title="No events match your current filters" description="Try a different event type or state. New campaign visits are announced every week." actionLabel="Clear filters" onAction={reset} data-id="element-823" data-mp="zvjj0bx1sbn6" />
            </div> : <ul className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-824" data-mp="c5z0tv8pxiey">
              {filtered.map((event, index) => <li key={event.slug} data-id="element-825" data-mp="gyiaih4abteh" data-mpi={index}>
                  <Reveal delay={Math.min(index, 5) * 0.03} className="h-full" data-id="element-826" data-mp="282r97vfiviy">
                    <article className="group flex h-full flex-col overflow-hidden rounded-card border border-sand-200 bg-white transition-[border-color,box-shadow] duration-200 ease-emphasis hover:border-green-200 hover:shadow-card" data-id="element-827" data-mp="25ydzzbn4k60">
                      <div className="relative overflow-hidden" data-id="element-828" data-mp="3tevmlv3jk0fk">
                        <img src={event.image} alt="" loading="lazy" className="aspect-[16/10] w-full object-cover transition-transform duration-300 ease-emphasis group-hover:scale-[1.02]" data-id="element-829" data-mp="3tg6sq1kglk9u" />
                        <span className="absolute left-4 top-4 rounded bg-ink/85 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.1em] text-white" data-id="element-830" data-mp="3erx08ejjjw7p">
                          {event.type}
                        </span>
                      </div>
                      <div className="flex flex-1 flex-col p-6" data-id="element-831" data-mp="3opbeq8lwmaxd">
                        <h2 className="font-display text-[20px] leading-snug text-ink" data-id="element-832" data-mp="6c3hru5ngao0">
                          {event.title}
                        </h2>
                        <dl className="mt-4 space-y-2 text-[14px] text-slate-600" data-id="element-833" data-mp="2cmbwzmb9bib">
                          <div className="flex items-center gap-2.5" data-id="element-834" data-mp="gaaw058k0emb">
                            <dt className="sr-only" data-id="element-835" data-mp="bbzmxwq32855">Date</dt>
                            <CalendarIcon aria-hidden className="h-4 w-4 text-green-600" data-id="element-836" data-mp="352wja0f7ia3n" />
                            <dd data-id="element-837" data-mp="394httf49qcao">{event.date}</dd>
                          </div>
                          <div className="flex items-center gap-2.5" data-id="element-838" data-mp="392f7b52br1ap">
                            <dt className="sr-only" data-id="element-839" data-mp="3e2t5lve4iwvj">Time</dt>
                            <ClockIcon aria-hidden className="h-4 w-4 text-green-600" data-id="element-840" data-mp="3iu1g6ur6woaa" />
                            <dd data-id="element-841" data-mp="3nteau1oayznf">{event.time}</dd>
                          </div>
                          <div className="flex items-start gap-2.5" data-id="element-842" data-mp="3nvicse2n5ol2">
                            <dt className="sr-only" data-id="element-843" data-mp="3sr60sq5e0boz">Venue</dt>
                            <MapPinIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-green-600" data-id="element-844" data-mp="o6kkdmhh4qg" />
                            <dd data-id="element-845" data-mp="22gb9rhcllnxh">
                              {event.venue}, {event.city}
                            </dd>
                          </div>
                        </dl>
                        <Link to={`/events/${event.slug}`} className="mt-auto inline-flex items-center gap-2 pt-6 text-[14px] font-semibold text-green-700" data-id="element-846" data-mp="27gz6mjnicyzm">
                          {event.past ? 'View event recap' : 'View event & RSVP'}
                          <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-200 ease-emphasis group-hover:translate-x-1" data-id="element-847" data-mp="1l70mtq9d7p7r" />
                        </Link>
                      </div>
                    </article>
                  </Reveal>
                </li>)}
            </ul>}
        </div>
      </section>

      <CtaBand title="Want the campaign in your state?" description="Local organisers request visits every week. Register as a coordinator and put your ward forward." primary={{
      label: 'Become a coordinator',
      to: '/join'
    }} secondary={{
      label: 'Contact the events team',
      to: '/contact'
    }} data-id="element-848" data-mp="24ty8hsq2w9cg" />
    </>;
}